import shortid from 'shortid';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import cloneDeep from 'lodash/cloneDeep';
import maxBy from 'lodash/maxBy';
import sum from 'lodash/sum';
import isEqual from 'lodash/isEqual';
import difference from 'utils/difference';
import {put, fork, take, select, race} from 'redux-saga/effects';
import {takeFetch} from 'redux/helpers';
import * as serviceActions from '../../../../services/actions';
import * as actions from './actions';
import currentTemplate from './currentTemplate/saga';
import {getTotalChanges} from '../../../../../helpers';


function* upsertTemplate() {
  const {success} = yield takeFetch(serviceActions.api.configuration.project.UPSERT_TEMPLATE);
  if (success) {
    let {templateOptions, currentTemplate} = yield select(state => state.configuration.project.editor.template);
    yield put(actions.save.end({
      changes: getTotalChanges(templateOptions) - currentTemplate.changes,
      id: success.data.id
    }));
    if (currentTemplate.value.isRemoved) {
      if (templateOptions.length) {
        yield put(actions.currentTemplate.init.begin(templateOptions[0].$$localId));
      } else {
        yield put(actions.currentTemplate.empty());
      }
    }
    ttb.Notifications.ShowSaveSuccessMessage('Project Configuration', 'All the changes have been saved');
  } else {
    ttb.Notifications.ShowErrorMessage('Project Configuration', 'The changes have not been saved');
  }
}

function* getTemplates() {
  const {success} = yield takeFetch(serviceActions.api.configuration.project.GET_TEMPLATES);
  if (success) {
    const {categories, itemOptions, subItemOptions, templates, users} = success.data;
    templates.sort((a, b) => a.order - b.order);
    const templateOptions = templates.map(createTemplateOption);

    yield put(actions.init.end({categories, templateOptions, itemOptions, subItemOptions, templates, users}));
    const option = templateOptions[0];
    const localId = option && option.$$localId;
    yield put(actions.currentTemplate.init.begin(localId));
  }
}

function createTemplateOption(template) {
  const newValue = cloneDeep(template);
  return {
    value: newValue,
    $$localId: shortid.generate(),
    changes: 0,
  };
}

function* init() {
  while (true) {
    const {projectId} = yield take(actions.INIT.BEGIN);
    yield put(serviceActions.api.configuration.project.getTemplates.request({
      projectId,
    }));
    yield fork(getTemplates);
  }
}

function* save() {
  while (true) {
    yield take(actions.SAVE.BEGIN);
    yield put(actions.currentTemplate.checkValidation.begin());
    const {errors} = yield take(actions.currentTemplate.CHECK_VALIDATION.END);
    if (!errors) {
      const {
        projectId,
        currentTemplate: selectedTemplate,
      } = yield select(state => state.configuration.project.editor.template);
      yield fork(upsertTemplate);
      yield put(serviceActions.api.configuration.project.upsertTemplate.request({
        projectId: +projectId,
        itemTemplate: selectedTemplate.value,
      }));
    }
  }
}

function* add() {
  while (true) {
    yield take(actions.ADD.BEGIN);
    const {templateOptions: previousTemplates, itemOptions} = yield select(state => state.configuration.project.editor.template);
    const maxOrderTemplate = maxBy(previousTemplates, x => x.value.order);
    const order = maxOrderTemplate ? maxOrderTemplate.value.order + 1 : 1;
    const localId = shortid.generate();
    const templateOptions = [
      ...previousTemplates,
      {
        $$localId: localId,
        added: true,
        changes: 0,
        value: {
          order,
          templateName: `New Template ${previousTemplates.length + 1}`,
          isRemoved: false,
          projectItemTypeConfigId: itemOptions.length > 0 ? itemOptions[0].id : null,
          templateItems: []
        },
      },
    ];
    yield put(actions.add.end({templateOptions}));
    yield put(actions.currentTemplate.init.begin(localId));
    yield put(actions.currentTemplate.addItem.begin());
  }
}

function* remove() {
  while (true) {
    yield take(actions.REMOVE);
    const {currentTemplate, templateOptions} = yield select(state => state.configuration.project.editor.template);
    if (currentTemplate.added) {
      yield put(actions.currentTemplate.init.begin(templateOptions[templateOptions.length - 1] && templateOptions[templateOptions.length - 1].$$localId));
    }
  }
}

function* invalidate() {
  while (true) {
    yield race([
      take(actions.currentTemplate.SET_NAME),
      take(actions.currentTemplate.SET_ORDER),
      take(actions.currentTemplate.SET_APPLICABLE),
      take(actions.ADD.END),
      take(actions.REMOVE),
      take(actions.currentTemplate.ADD_ITEM.END),
      take(actions.currentTemplate.REMOVE_ITEM),
      take(actions.currentTemplate.SET_ITEM_VALUE),
      take(actions.currentTemplate.RESET_CHANGES.END),
    ]);
    const {
      templates,
      templateOptions: previousTemplates,
      currentTemplate: selectedTemplate,
    } = yield select(state => state.configuration.project.editor.template);
    const newTemplates = previousTemplates.map((x) => {
      const res = x.$$localId === selectedTemplate.$$localId && !isEqual(x.value, selectedTemplate.value)
        ? {...selectedTemplate}
        : x;
      const templateValue = find(templates, y => y.id === res.value.id);
      const changes = templateValue ? difference(res.value, templateValue) : 1;
      return res.changes === changes ? res : {
        ...res,
        changes,
      };
    });
    const templateOptions = isEqual(newTemplates, previousTemplates)
      ? previousTemplates
      : newTemplates;
    const allChanges = getTotalChanges(templateOptions);
    yield put(actions.invalidate({templateOptions, changes: allChanges}));

    const newSelectedTemplate = find(templateOptions, x => x.$$localId === selectedTemplate.$$localId);
    if (newSelectedTemplate && newSelectedTemplate.changes !== selectedTemplate.changes) {
      yield put(actions.currentTemplate.invalidateChanges({changes: newSelectedTemplate.changes}));
    }
  }
}


export default function* template() {
  yield fork(currentTemplate);
  yield fork(init);
  yield fork(save);
  yield fork(add);
  yield fork(remove);
  yield fork(invalidate);
}



// WEBPACK FOOTER //
// ../redux/store/configuration/project/editor/template/saga.js